import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '#services';

@Component({
  selector: 'app-top-nav-bar',
  imports: [TranslatePipe],
  templateUrl: './top-nav-bar.html',
  styleUrl: './top-nav-bar.scss',
})
export class TopNavBar {
  private _authService = inject(AuthService);
  private _dialogService = inject(DialogService);
  private _translateService = inject(TranslateService);
  private _destroyRef = inject(DestroyRef);

  isMobile = input(false);
  menuToggle = output<void>();

  private _accountMenuHost =
    viewChild<ElementRef<HTMLElement>>('accountMenuHost');
  private _accountMenuOpen = signal(false);
  private _profileDialogRef: DynamicDialogRef | null = null;

  private _userDetails = toSignal(this._authService.userDetails$, {
    initialValue: null,
  });

  accountMenuOpen = this._accountMenuOpen.asReadonly();
  userDetails = this._userDetails;
  userDisplayName = computed(
    () => this._userDetails()?.username ?? this._userDetails()?.email ?? 'User',
  );
  userEmail = computed(() => this._userDetails()?.email ?? '');
  userRole = computed(() => this.isAuthorized() ? this._formatRole(this._userDetails()?.role) : '');
  hasRole = computed(() => !!this.userRole());
  isAuthorized = computed(() => !!this._userDetails()?.hasAccess);
  avatarInitials = computed(() => this._buildInitials(this.userDisplayName()));

  toggleAccountMenu(): void {
    this._accountMenuOpen.update((open) => !open);
  }

  async openProfileDialog(): Promise<void> {
    this._accountMenuOpen.set(false);

    if (!this.isAuthorized() || this._profileDialogRef) {
      return;
    }

    const { ProfileConfiguration } =
      await import('../../features/profile-configuration/profile-configuration');

    const ref = this._dialogService.open(ProfileConfiguration, {
      closable: true,
      modal: true,
      styleClass: 'centaur-dialog centaur-dialog--profile',
      header: this._translateService.instant('profileConfiguration.title'),
    });

    if (!ref) {
      return;
    }

    this._profileDialogRef = ref;
    ref.onClose.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this._profileDialogRef = null;
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const accountMenuHost = this._accountMenuHost()?.nativeElement;

    if (
      this._accountMenuOpen() &&
      accountMenuHost &&
      event.target instanceof Node &&
      !accountMenuHost.contains(event.target)
    ) {
      this._accountMenuOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this._accountMenuOpen.set(false);
  }

  private _buildInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);

    if (parts.length === 0) {
      return 'U';
    }

    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }

  private _formatRole(role?: string): string {
    if (!role) {
      return '';
    }

    return role
      .replace(/[_-]/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .trim();
  }
}
